import { useEffect, useState } from 'react';
import {
  ThermometerSun,
  Droplets,
  CloudRain,
  Wind,
  RotateCw,
  Trash2,
  X,
  ExternalLink,
  MapPin,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import type { Field } from '../../types';
import {
  formatGpsCoords,
  getFieldCentroid,
  formatJagtPoiNotes,
  type JagtPoiConfig,
  type JagtPoiData,
} from '../../lib/poi';
import { fetchSoilWeatherData, type SoilWeatherData } from '../../lib/weather';

interface Props {
  field: Field;
  poi: JagtPoiData;
  poiConfig: JagtPoiConfig;
  planReadOnly: boolean;
  updateField: (id: string, patch: Partial<Field>) => void;
  deleteField: (field: Field) => void;
  onClose: () => void;
  onNavigateToSeeding?: () => void;
  notify?: (msg: string) => void;
}

export function PoiSoilSensorInspector({
  field,
  poi,
  poiConfig,
  planReadOnly,
  updateField,
  deleteField,
  onClose,
  onNavigateToSeeding,
  notify,
}: Props) {
  const [data, setData] = useState<SoilWeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const centroid = getFieldCentroid(field.geometry);
  const lat = centroid?.lat ?? 55.4;
  const lng = centroid?.lng ?? 11.8;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchSoilWeatherData(lat, lng)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lat, lng, refreshCount]);

  function handleRefresh() {
    if (typeof window !== 'undefined') {
      const key = `plotwise_soil_weather_${lat.toFixed(2)}_${lng.toFixed(2)}`;
      localStorage.removeItem(key);
    }
    setRefreshCount((c) => c + 1);
    if (notify) notify('Henter frisk måling for dette punkt...');
  }

  const soilTemp = data?.current.soilTemperature6cm ?? 0;
  const surfaceTemp = data?.current.soilTemperature0cm ?? 0;
  const airTemp = data?.current.temperature2m ?? 0;
  const moisturePct = Math.round((data?.current.soilMoisture0to1cm ?? 0) * 100);

  const isOptimal = soilTemp >= 10;
  const isPrep = soilTemp >= 7 && soilTemp < 10;
  const isCold = soilTemp < 7;

  return (
    <div className="field-inspector is-poi-inspector is-weather-sensor-inspector">
      <div className="sheet-handle" aria-hidden="true" />

      {/* Header */}
      <div className="section-heading">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-flex',
              color: poiConfig.color,
            }}
            dangerouslySetInnerHTML={{ __html: poiConfig.svgIcon }}
          />
          {field.name || poiConfig.label}
        </h3>
        <button className="icon-button" aria-label="Luk oplysninger" onClick={onClose}>
          <X size={17} />
        </button>
      </div>

      {/* GPS Pinpoint Banner */}
      <div
        style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 8,
          padding: '10px 12px',
          marginBottom: 14,
          fontSize: 12,
          color: '#0369a1',
          lineHeight: 1.4,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
          <MapPin size={13} style={{ color: '#0369a1', flexShrink: 0 }} />
          <span>Lokal mikroklima-måling</span>
        </div>
        <div style={{ marginTop: 2, color: '#0c4a6e' }}>
          GPS: <strong>{formatGpsCoords(lat, lng)}</strong>
        </div>
        <div style={{ marginTop: 4, fontSize: 11, color: '#38bdf8' }}>
          DWD ICON &amp; ECMWF landbrugsmodel (1-2 km grid)
        </div>
        <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: '#0369a1' }}>
          💡 Træk i punktet på kortet for at flytte placering
        </div>
      </div>

      {/* Live Måler Resultat Boks */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 14,
          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
            Realtids jorddata (lige her)
          </span>
          <button
            type="button"
            className="link-button"
            onClick={handleRefresh}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11,
              color: '#0369a1',
            }}
          >
            <RotateCw size={12} className={loading ? 'animate-spin' : ''} />
            <span>Opdater</span>
          </button>
        </div>

        {loading && !data ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: '#64748b', fontSize: 12 }}>
            <RotateCw
              size={18}
              className="animate-spin"
              style={{ margin: '0 auto 6px', color: '#0369a1' }}
            />
            Henter jordtemperatur for punktet...
          </div>
        ) : (
          <div>
            {/* Primær KPI: Jordtemperatur i 6 cm */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                background: '#f8fafc',
                borderRadius: 8,
                marginBottom: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                  Jordtemperatur (6 cm såbed)
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                  {soilTemp.toFixed(1)} °C
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: 6,
                  background: isOptimal ? '#dcfce7' : isPrep ? '#fef9c3' : '#e0f2fe',
                  color: isOptimal ? '#166534' : isPrep ? '#854d0e' : '#0369a1',
                }}
              >
                {isOptimal ? 'Optimal' : isPrep ? 'Forbered' : 'For kold'}
              </span>
            </div>

            {/* Sekundære målinger */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 6,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  background: '#f8fafc',
                  padding: '6px 4px',
                  borderRadius: 6,
                  border: '1px solid #f1f5f9',
                }}
              >
                <div style={{ fontSize: 10, color: '#64748b' }}>Overflade</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  {surfaceTemp.toFixed(1)} °C
                </div>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  padding: '6px 4px',
                  borderRadius: 6,
                  border: '1px solid #f1f5f9',
                }}
              >
                <div style={{ fontSize: 10, color: '#64748b' }}>Fugtighed</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  {moisturePct} %
                </div>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  padding: '6px 4px',
                  borderRadius: 6,
                  border: '1px solid #f1f5f9',
                }}
              >
                <div style={{ fontSize: 10, color: '#64748b' }}>Luft</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  {airTemp.toFixed(1)} °C
                </div>
              </div>
            </div>

            {/* Agronomisk råd for dette punkt */}
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                background: isOptimal ? '#f0fdf4' : '#fffbeb',
                border: isOptimal ? '1px solid #bbf7d0' : '1px solid #fef3c7',
                borderRadius: 6,
                fontSize: 11,
                lineHeight: 1.4,
                color: isOptimal ? '#166534' : '#92400e',
              }}
            >
              <strong>Vurdering for dette punkt: </strong>
              {isOptimal
                ? 'Jorden er lun nok til både forårs- og efterårsetablering. Gode spiringsbetingelser.'
                : isPrep
                  ? 'Jorden er ved at varme op (7–10 °C). Ideel til jordbearbejdning og harvning. Afvent et par lune dage for optimal spiring.'
                  : 'Jorden er kold (< 7 °C). Faunamaster anbefaler at vente med såning for at undgå at frøene rådner i jorden.'}
            </div>

            {/* 7-dages udsigt for punktet */}
            {data?.daily && data.daily.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#475569',
                    marginBottom: 6,
                  }}
                >
                  Kommende 7 dages jordtemperatur:
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    overflowX: 'auto',
                    paddingBottom: 4,
                  }}
                >
                  {data.daily.slice(0, 7).map((d) => {
                    const dayName = new Date(d.date).toLocaleDateString('da-DK', {
                      weekday: 'short',
                    });
                    return (
                      <div
                        key={d.date}
                        style={{
                          flex: '0 0 auto',
                          minWidth: 42,
                          textAlign: 'center',
                          padding: '4px 2px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 6,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: '#64748b',
                            textTransform: 'capitalize',
                          }}
                        >
                          {dayName}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: d.soilTemp6cmAvg >= 10 ? '#15803d' : '#0f172a',
                            marginTop: 2,
                          }}
                        >
                          {d.soilTemp6cmAvg.toFixed(0)}°
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Redigering af Navn */}
      <label className="field-label">
        Navn på {poiConfig.label.toLowerCase()}
        <input
          className="form-input"
          disabled={planReadOnly}
          value={field.name}
          onChange={(e) => updateField(field.id, { name: e.target.value })}
          onBlur={() => {
            if (!field.name.trim()) updateField(field.id, { name: poiConfig.defaultName });
          }}
        />
      </label>

      {/* Noter om placeringen */}
      <label className="field-label" style={{ marginTop: 12 }}>
        Noter om dette målepunkt (jordtype, mikroklima)
        <textarea
          className="form-input"
          disabled={planReadOnly}
          rows={2}
          value={poi.userNotes}
          onChange={(e) => {
            const newNotes = formatJagtPoiNotes(poi.type, undefined, e.target.value);
            updateField(field.id, { notes: newNotes });
          }}
          placeholder="F.eks. sandet jord, høj beliggenhed, tidlig forårsvarme…"
        />
      </label>

      {/* Handlinger */}
      {onNavigateToSeeding && (
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            className="button secondary"
            style={{
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: 12,
            }}
            onClick={onNavigateToSeeding}
          >
            <span>Gå til Såningsgennemgang</span>
            <ExternalLink size={13} />
          </button>
        </div>
      )}

      <div className="inspector-foot" style={{ marginTop: 14 }}>
        <button
          className="text-button danger"
          disabled={planReadOnly}
          onClick={() => deleteField(field)}
        >
          <Trash2 size={14} />
          Slet {poiConfig.label.toLowerCase()}
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className="button primary"
          style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
          onClick={onClose}
        >
          Luk måler
        </button>
      </div>
    </div>
  );
}
